// The platform's transport for Make changes (packages/page-runtime/src/
// customize, scope 'app'): the /designers/transaction/<project>/<obj>/build-*
// endpoints, bound to one project. Each step is checked against the model's
// hash as last seen (the designer's own conflict rule), so a change made
// meanwhile in the designer, by the AI or by another person is never
// overwritten — the step is refused and the screen asks for a reload.

import type { BuildSources, CustomizeTransport, ModelOverlayResult } from '@twasta/page-runtime/customize'
import type { BuildOps } from '@twasta/page-runtime/customize'
import {
  makeChangesFieldUsage, makeChangesModel, makeChangesPatch, makeChangesSources,
} from '@/api/designers'
import { listStarterTemplates, saveStarterTemplate } from '@/api/messaging'

export function platformBuildTransport(project: string): CustomizeTransport & BuildOps {
  const hashes = new Map<string, string | null>()
  let sources: Promise<BuildSources> | null = null
  return {
    get: async (_objType, objName) => {
      const r = await makeChangesModel(project, objName)
      hashes.set(objName, r.version_hash)
      return r as ModelOverlayResult
    },
    patch: async (_objType, objName, set, reset, extras) => {
      const r = await makeChangesPatch(project, objName, {
        set, reset, ...(extras || {}), base_hash: hashes.get(objName) || undefined,
      })
      hashes.set(objName, r.version_hash)
      return r
    },
    // Make changes offers no "Reset to standard": there is no standard
    // under the core model. Undo and Versions cover going back.
    reset: () => Promise.reject(new Error('Use Undo or Versions to go back')),
    fieldUsage: (objName, formNo, colKey) => makeChangesFieldUsage(project, objName, formNo, colKey),
    sources: () => {
      if (!sources) {
        sources = makeChangesSources(project) as Promise<BuildSources>
        sources.catch(() => { sources = null })
      }
      return sources
    },
    // After saving… sends a starter template saved under this screen.
    saveTemplate: async (objName, template) => {
      const saved = await saveStarterTemplate(project, objName, template)
      return { id: saved?.id || template.id }
    },
    listTemplates: (objName) => listStarterTemplates(project, objName),
  }
}
